'use client';

import { useCallback, useState } from 'react';
import type { Mensagem } from '@/features/messaging';
import { ROTULO_DO_TIPO_DE_MIDIA } from '@/shared/constants';
import { ChatMessageOptions } from '@/shared/components/ui/common/ChatMessageOptions';
import { baixarArquivoRemoto } from '@/shared/utils';
import { FeatherCpu, FeatherDownload, FeatherFileText, FeatherUser } from '@subframe/core';

import { ROTULO_VOCE } from '../../../utils/autoriaMensagem';
import { chatMessageButtons } from '@/shared/utils/chatMessageButtons';
import { extrairExtensao } from '../../../utils/negociacaoDetailUiUtils';

interface NegociacaoChatMessageProps {
  sender: string;
  text: string;
  metadata?: unknown;
  time: string;
  isIA?: boolean;
  isOutbound?: boolean;
  muted?: boolean;
  type: Mensagem['tipo'];
  mediaUrl?: string | null;
}

/** Texto e sistema não têm rótulo de mídia: caem no 'Arquivo'. */
const ROTULO_POR_TIPO_DE_MENSAGEM: Partial<Record<Mensagem['tipo'], string>> =
  ROTULO_DO_TIPO_DE_MIDIA;

function getExtensionLabel(url: string): string {
  const ext = extrairExtensao(url);
  const labels: Record<string, string> = {
    pdf: 'PDF',
    doc: 'DOC',
    docx: 'DOCX',
    xls: 'XLS',
    xlsx: 'XLSX',
    jpg: 'JPG',
    jpeg: 'JPG',
    png: 'PNG',
    gif: 'GIF',
    webp: 'WEBP',
    mp3: 'MP3',
    ogg: 'OGG',
    wav: 'WAV',
    mp4: 'MP4',
    '3gp': '3GP',
    mov: 'MOV',
  };
  return (ext && labels[ext]) || '';
}

function getDisplayName(url: string, tipo: Mensagem['tipo']): string {
  const ext = getExtensionLabel(url);
  const base = ROTULO_POR_TIPO_DE_MENSAGEM[tipo] || 'Arquivo';
  return ext ? `${base}.${ext.toLowerCase()}` : base;
}

function getAutoriaLabel(sender: string, isIA?: boolean): string {
  if (isIA) return 'Lexi · Agente IA';
  if (sender === ROTULO_VOCE) return 'Você · Intervenção humana';
  return sender;
}

function getBubbleClass(isMe: boolean, isDarkBubble: boolean, muted?: boolean): string {
  if (isDarkBubble) {
    return 'bg-brand-600 rounded-tr-none text-white';
  }
  if (isMe) {
    return muted
      ? 'bg-neutral-200 rounded-tr-none text-default-font'
      : 'border border-brand-200 bg-brand-50 rounded-tr-none text-default-font';
  }
  return 'text-default-font rounded-tl-none border border-neutral-200 bg-white';
}

export function NegociacaoChatMessage({
  sender,
  text,
  metadata,
  time,
  isIA,
  isOutbound,
  muted,
  type,
  mediaUrl,
}: NegociacaoChatMessageProps) {
  const isMe = Boolean(isOutbound) || sender === ROTULO_VOCE || Boolean(isIA);
  const isDarkBubble = Boolean(isIA) && !muted;
  const autoria = getAutoriaLabel(sender, isIA);
  const hasText = text.trim().length > 0;
  const choices = isOutbound ? chatMessageButtons(metadata) : null;
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const handleDownload = useCallback((url: string, name: string) => {
    void baixarArquivoRemoto(url, name);
  }, []);

  const renderMedia = () => {
    if (!mediaUrl) {
      if (!hasText && type !== 'TEXT') {
        return <span className="text-caption text-neutral-500 italic">Mídia indisponível.</span>;
      }
      return null;
    }

    if (type === 'AUDIO') {
      return <audio className="w-64 max-w-full" controls src={mediaUrl} />;
    }

    if (type === 'IMAGE') {
      const fileName = getDisplayName(mediaUrl, type);

      if (imgError) {
        return (
          <button
            type="button"
            onClick={() => handleDownload(mediaUrl, fileName)}
            className={`flex items-center gap-2 rounded-lg p-3 transition-colors ${isDarkBubble ? 'bg-white/10 hover:bg-white/20' : 'bg-neutral-50 hover:bg-neutral-100'}`}
          >
            <FeatherDownload
              className={`h-4 w-4 ${isDarkBubble ? 'text-white/70' : 'text-neutral-500'}`}
            />
            <span className={`text-[13px] ${isDarkBubble ? 'text-white/80' : 'text-neutral-600'}`}>
              Imagem indisponível — clique para baixar
            </span>
          </button>
        );
      }

      return (
        <div className="group relative">
          {!imgLoaded && (
            <div className="flex h-[180px] w-[240px] items-center justify-center rounded-lg bg-neutral-100">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-600" />
            </div>
          )}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={mediaUrl}
            alt="Imagem enviada"
            className={`max-w-[240px] cursor-pointer rounded-lg object-cover ${imgLoaded ? '' : 'hidden'}`}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
            onClick={() => window.open(mediaUrl, '_blank', 'noopener,noreferrer')}
          />
          {imgLoaded && (
            <button
              type="button"
              onClick={() => handleDownload(mediaUrl, fileName)}
              className="absolute top-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100 hover:bg-black/70"
            >
              <FeatherDownload className="h-4 w-4" />
            </button>
          )}
        </div>
      );
    }

    if (type === 'VIDEO') {
      const fileName = getDisplayName(mediaUrl, type);

      if (videoError) {
        return (
          <button
            type="button"
            onClick={() => handleDownload(mediaUrl, fileName)}
            className={`flex items-center gap-2 rounded-lg p-3 transition-colors ${isDarkBubble ? 'bg-white/10 hover:bg-white/20' : 'bg-neutral-50 hover:bg-neutral-100'}`}
          >
            <FeatherDownload
              className={`h-4 w-4 ${isDarkBubble ? 'text-white/70' : 'text-neutral-500'}`}
            />
            <span className={`text-[13px] ${isDarkBubble ? 'text-white/80' : 'text-neutral-600'}`}>
              Vídeo indisponível — clique para baixar
            </span>
          </button>
        );
      }

      return (
        <div className="group relative">
          <video
            className="max-w-[280px] rounded-lg"
            controls
            preload="metadata"
            src={mediaUrl}
            onError={() => setVideoError(true)}
          />
          <button
            type="button"
            onClick={() => handleDownload(mediaUrl, fileName)}
            className="absolute top-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100 hover:bg-black/70"
          >
            <FeatherDownload className="h-4 w-4" />
          </button>
        </div>
      );
    }

    if (type === 'DOCUMENT') {
      const fileName = getDisplayName(mediaUrl, type);
      const extLabel = getExtensionLabel(mediaUrl);
      return (
        <button
          type="button"
          onClick={() => handleDownload(mediaUrl, fileName)}
          className={`flex w-full items-center gap-3 rounded-lg p-3 transition-colors ${isDarkBubble ? 'bg-white/10 hover:bg-white/20' : 'bg-neutral-50 hover:bg-neutral-100'}`}
        >
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${isDarkBubble ? 'bg-white/20 text-white' : 'bg-neutral-200 text-neutral-600'}`}
          >
            <FeatherFileText className="h-5 w-5" />
          </div>
          <div className="flex flex-1 flex-col items-start overflow-hidden">
            <span
              className={`truncate text-[13px] font-medium ${isDarkBubble ? 'text-white' : 'text-default-font'}`}
            >
              {fileName}
            </span>
            {extLabel && (
              <span
                className={`text-[11px] ${isDarkBubble ? 'text-white/70' : 'text-neutral-400'}`}
              >
                {extLabel}
              </span>
            )}
          </div>
          <FeatherDownload
            className={`h-4 w-4 shrink-0 ${isDarkBubble ? 'text-white/70' : 'text-neutral-400'}`}
          />
        </button>
      );
    }

    return null;
  };

  return (
    <div className={`flex w-full flex-col gap-1 ${isMe ? 'items-end' : 'items-start'}`}>
      <div
        className={`flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 px-1 ${isMe ? 'justify-end' : ''}`}
      >
        <span
          className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${isMe && !muted ? 'text-brand-700' : 'text-subtext-color'}`}
        >
          {isIA ? (
            <FeatherCpu aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
          ) : isMe ? (
            <FeatherUser aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
          ) : null}
          {autoria}
        </span>
        <span className="text-subtext-color text-[11px]">{time}</span>
      </div>
      <div
        className={`text-body-sm max-w-[80%] rounded-[12px] px-4 py-2.5 ${getBubbleClass(isMe, isDarkBubble, muted)}`}
      >
        <div className="flex flex-col gap-2">
          {hasText ? <span className="break-words whitespace-pre-wrap">{text}</span> : null}
          {!hasText && !mediaUrl && !choices && type === 'TEXT' ? (
            <span className="text-caption text-neutral-500 italic">Mensagem sem conteúdo.</span>
          ) : null}
          {renderMedia()}
          {choices ? <ChatMessageOptions metadata={metadata} onDark={isDarkBubble} /> : null}
        </div>
      </div>
    </div>
  );
}
